import React from 'react';
import { MyDataGrid, MyTypography } from '../../common/components';
import { IconButton, Stack } from '@mui/material';
import { IconDeviceLandlinePhone } from '@tabler/icons';
import { isEmpty } from 'lodash';
import DateTimeUtils from '../../common/services/DateTimeUtils';
import useCurrentClinicTimeFormat from '../../common/hooks/useCurrentClinicTimeFormat';

const shouldIncludeExtras = true;
const columns = [
  {
    field: 'id',
    headerName: 'Fax ID',
    sortable: true,
    editable: false,
    filterable: true,
    width: 240,
    valueGetter: params => {
      return params.row.id;
    },
  },
  {
    field: 'referrer',
    headerName: 'Clinic Sender',
    sortable: true,
    editable: false,
    filterable: true,
    width: 250,
    renderCell: params => {
      return (
        <Stack direction="row" spacing={1}>
          <MyTypography variant="subtitle1">
            {!isEmpty(params.row.referrer) ? params.row.referrer.name : ''}
          </MyTypography>
        </Stack>
      );
    },
  },
  {
    field: 'receiver',
    headerName: 'Clinic Receiver',
    sortable: true,
    editable: false,
    filterable: true,
    width: 250,
    renderCell: params => {
        return (
          <Stack direction="row" spacing={1}>
            <IconDeviceLandlinePhone />
            <MyTypography variant="subtitle1">{!isEmpty(params.row.receiver) ? params.row.receiver.name:""}</MyTypography>
          </Stack>
        );
      },
  },
  {
    field: 'status',
    headerName: 'Status',
    sortable: true,
    editable: false,
    filterable: false,
    width: 200,
    valueGetter: params => {
      return params.row.status;
    },
  },
  {
    field: 'pagesSent',
    headerName: 'Pages Sent',
    sortable: true,
    editable: false,
    filterable: false,
    width: 100,
    valueGetter: params => {
      return params.row.pagesSent;
    },
  },
  {
    field: 'submitTime',
    headerName: 'Submit Time',
    sortable: true,
    editable: false,
    filterable: false,
    width: 250,
    valueGetter: params => {
      return params.row.submitTime;
    },
    sortComparator: DateTimeUtils.dateSortComparator,
  },
  {
    field: 'completionTime',
    headerName: 'Last Update Time',
    sortable: true,
    editable: false,
    filterable: false,
    width: 250,
    valueGetter: params => {
      return params.row.completionTime;
    },
    sortComparator: DateTimeUtils.dateSortComparator,
  },
];

const finalColumns = shouldIncludeExtras
  ? [
    ...columns,
    {
      field: 'attemptsMade',
      headerName: 'Attempts Made',
      sortable: true,
      editable: false,
      filterable: false,
      width: 150,
      valueGetter: params => params.row.attemptsMade,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 210,
      headerAlign: 'center',
      sortable: false,
      editable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: params => (
        <Stack direction="row" spacing={1} sx={{ m: 'auto' }}>
          <IconButton
            onClick={e => {
              e.stopPropagation();
              params.row.actions.onEdit(params.row.id);
            }}
          >
            <IconDeviceLandlinePhone />
          </IconButton>
        </Stack>
      ),
    },
  ] : columns;

export default function FaxTable({ data, loading, onAdd, showToolbar = true }) {
  const currentClinicTimeFormat = useCurrentClinicTimeFormat();

  const columnsWithTimeFormat = finalColumns.map(column => {
    if (column.field === 'submitTime') {
      return {
        ...column,
        valueGetter: params => DateTimeUtils.formatDisplayDateTime(
          params.row.submitTime,
          currentClinicTimeFormat,
          true,
        ),
      };
    }

    if (column.field === 'completionTime') {
      return {
        ...column,
        valueGetter: params => DateTimeUtils.formatDisplayDateTime(
          params.row.completionTime,
          currentClinicTimeFormat,
          true,
        ),
      };
    }

    return column;
  });

  return (
    <MyDataGrid
      onAdd={onAdd}
      loading={loading}
      data={data}
      columns={columnsWithTimeFormat}
      showToolbar={showToolbar}
      getRowId={row => {
        return row.id;
      }}
      minTableHeight={'calc(100vh - 282px)'}
    />
  );
}
